import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import PageLayout from '../../components/layout/PageLayout';
import { useGetClientsAsPerStatusQuery } from '../../store/api';
import { toast } from 'react-toastify';
import { nowDateLocal, startOfMonthDateLocal } from '../../utils/datetimeLocal';

/** Default filter: first day of this month → today (local), so the report loads on open */
function defaultDateRange() {
  return { from_date: startOfMonthDateLocal(), to_date: nowDateLocal() };
}

/** Normalize API payload to an array of row objects */
function normalizeRows(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === 'object') {
    if (Array.isArray(payload.list)) return payload.list;
    if (Array.isArray(payload.data)) return payload.data;
    if (Array.isArray(payload.rows)) return payload.rows;
    if (Array.isArray(payload.clients)) return payload.clients;
  }
  return [];
}

function deriveColumns(rows) {
  if (!rows.length) return [];
  const keys = new Set();
  rows.forEach((r) => {
    if (r && typeof r === 'object' && !Array.isArray(r)) Object.keys(r).forEach((k) => keys.add(k));
  });
  return [...keys];
}

/** Status first, then YYYY-MM months, then Total; append any other keys. */
function orderColumns(keys) {
  if (!keys.length) return [];
  const lowerMap = new Map(keys.map((k) => [k.toLowerCase(), k]));
  const findKey = (...names) => names.map((n) => lowerMap.get(n.toLowerCase())).find(Boolean);
  const labelKey =
    findKey('status', 'lead_status', 'name', 'label') ||
    keys.find((k) => !/^\d{4}-\d{2}$/.test(k) && !/^total$/i.test(k));
  const months = keys.filter((k) => /^\d{4}-\d{2}$/.test(k)).sort();
  const totalKey = keys.find((k) => /^total$/i.test(k));
  const ordered = [];
  if (labelKey && keys.includes(labelKey)) ordered.push(labelKey);
  months.forEach((m) => {
    if (m !== labelKey && !ordered.includes(m)) ordered.push(m);
  });
  if (totalKey && !ordered.includes(totalKey)) ordered.push(totalKey);
  keys.forEach((k) => {
    if (!ordered.includes(k)) ordered.push(k);
  });
  return ordered;
}

function columnIsSummable(rows, key) {
  if (!rows.length) return false;
  let numericCount = 0;
  for (const row of rows) {
    const v = row[key];
    if (v === undefined || v === null || v === '') continue;
    const n = Number(v);
    if (Number.isFinite(n)) numericCount++;
    else if (typeof v === 'string' && String(v).trim() !== '') return false;
  }
  return numericCount > 0;
}

function sumColumn(rows, key) {
  return rows.reduce((acc, row) => {
    const v = row[key];
    const n = Number(v);
    return acc + (Number.isFinite(n) ? n : 0);
  }, 0);
}

function findLabelKey(keys) {
  if (!keys?.length) return null;
  const lowerMap = new Map(keys.map((k) => [k.toLowerCase(), k]));
  const fromNames = ['status', 'lead_status', 'name', 'label']
    .map((n) => lowerMap.get(n))
    .find(Boolean);
  return (
    fromNames ||
    keys.find((k) => !/^\d{4}-\d{2}$/.test(k) && !/^total$/i.test(k)) ||
    null
  );
}

/** Backend may include an aggregate row — exclude from table and from sums */
function isSummaryRow(row, labelKey) {
  if (!row || !labelKey) return false;
  const label = row[labelKey];
  return /^total$/i.test(String(label ?? '').trim());
}

function filterDataRows(rows, labelKey) {
  const key = labelKey ?? findLabelKey(deriveColumns(rows));
  if (!key) return rows;
  return rows.filter((r) => !isSummaryRow(r, key));
}

function formatTotalCell(n) {
  if (!Number.isFinite(n)) return '—';
  return Number.isInteger(n) ? String(n) : String(n);
}

function labelize(key) {
  return String(key || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function ClientsAsPerStatus() {
  const [{ from_date, to_date }, setRange] = useState(() => defaultDateRange());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const skip = !from_date || !to_date;
  const { data, isFetching: loading, error } = useGetClientsAsPerStatusQuery(
    { from_date, to_date },
    { skip }
  );

  const rows = useMemo(() => {
    if (skip) return [];
    const raw = data?.data ?? data;
    const list = normalizeRows(raw);
    return Array.isArray(list) ? list : [];
  }, [data, skip]);

  useEffect(() => {
    if (!from_date || !to_date) {
      toast.warn('Select from and to dates');
    }
  }, [from_date, to_date]);

  useEffect(() => {
    if (error) {
      toast.error(error?.data?.message || 'Failed to load clients as per status');
    }
  }, [error]);

  useEffect(() => {
    setPage(0);
  }, [rows]);

  const columns = useMemo(() => deriveColumns(rows), [rows]);
  const labelKey = useMemo(() => findLabelKey(columns), [columns]);
  const dataRows = useMemo(() => filterDataRows(rows, labelKey), [rows, labelKey]);
  const orderedColumns = useMemo(() => orderColumns(columns), [columns]);

  const paginated = useMemo(
    () => dataRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [dataRows, page, rowsPerPage]
  );

  /** Footer totals: sum only rows currently shown in the table */
  const columnTotals = useMemo(() => {
    const totals = {};
    for (const key of orderedColumns) {
      if (columnIsSummable(paginated, key)) totals[key] = sumColumn(paginated, key);
    }
    return totals;
  }, [paginated, orderedColumns]);

  const firstNonSummableKey = useMemo(
    () => orderedColumns.find((c) => !columnIsSummable(paginated, c)),
    [orderedColumns, paginated]
  );

  return (
    <PageLayout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
          No. of Clients As Per Status
        </Typography>

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
              <TextField
                label="From date"
                type="date"
                size="small"
                value={from_date}
                onChange={(e) => setRange((r) => ({ ...r, from_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="To date"
                type="date"
                size="small"
                value={to_date}
                onChange={(e) => setRange((r) => ({ ...r, to_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent sx={{ p: 0 }}>
            <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.100' }}>
                    {orderedColumns.length === 0 ? (
                      <TableCell sx={{ fontWeight: 600 }}>—</TableCell>
                    ) : (
                      orderedColumns.map((c) => (
                        <TableCell
                          key={c}
                          align={columnIsSummable(paginated, c) ? 'right' : 'left'}
                          sx={{ fontWeight: 600, whiteSpace: 'nowrap', maxWidth: 280 }}
                        >
                          {labelize(c)}
                        </TableCell>
                      ))
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={Math.max(orderedColumns.length, 1)} align="center" sx={{ py: 6 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : paginated.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={Math.max(orderedColumns.length, 1)} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No data returned for the selected date range.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <>
                      {paginated.map((row, idx) => (
                        <TableRow key={idx} hover>
                          {orderedColumns.map((c) => {
                            const v = row[c];
                            const display =
                              v === undefined || v === null
                                ? '—'
                                : typeof v === 'object'
                                  ? JSON.stringify(v)
                                  : String(v);
                            const summable = columnIsSummable(paginated, c);
                            return (
                              <TableCell key={c} align={summable ? 'right' : 'left'} sx={{ maxWidth: 280 }}>
                                <Typography variant="body2" noWrap title={display}>
                                  {display}
                                </Typography>
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                      {!loading && paginated.length > 0 && orderedColumns.length > 0 && (
                        <TableRow
                          sx={{
                            backgroundColor: 'grey.100',
                            '& .MuiTableCell-root': {
                              fontWeight: 600,
                              borderTop: '2px solid',
                              borderColor: 'divider',
                            },
                          }}
                        >
                          {orderedColumns.map((c) => {
                            const summable = columnIsSummable(paginated, c);
                            if (summable) {
                              return (
                                <TableCell key={c} align="right" sx={{ maxWidth: 280 }}>
                                  {formatTotalCell(columnTotals[c])}
                                </TableCell>
                              );
                            }
                            if (c === firstNonSummableKey) {
                              return (
                                <TableCell key={c} align="left" sx={{ whiteSpace: 'nowrap', maxWidth: 280 }}>
                                  Total
                                </TableCell>
                              );
                            }
                            return (
                              <TableCell key={c} align="left" sx={{ maxWidth: 280 }}>
                                <Typography variant="body2" color="text.secondary">
                                  —
                                </Typography>
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      )}
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {!loading && dataRows.length > 0 && (
              <TablePagination
                component="div"
                count={dataRows.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50]}
              />
            )}
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
}
